import type { CompanySettings, Invoice, PacaSettings } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useAuth } from '../auth';
import { InvoicePaper, PACA_NOTICE } from '../components/InvoicePaper';
import { Dialog } from '../components/Dialog';
import { EmailPanel } from '../components/EmailPanel';
import { ShortsPanel } from '../components/ShortsPanel';
import { QboWriteChip, useBooksSwitched } from '../components/QuickBooksBand';
import { ErrorBox, PageHead, PracticeBanner, QueryView } from '../components/ui';
import { formatMoney, todayIso } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

export { PACA_NOTICE };

export const canKeepBooks = (role: string | undefined): boolean => role === 'owner' || role === 'office';
/** Edit shows on an issued invoice made in the app, for the owner or the office; one from an earlier month (it may be
 *  reported already) for the owner only (decisions 170, 173). The server still has the last word: a closed month, a
 *  date before the ledger start, a Square payment clearing or the money mirror refuse the save with the reason. */
export const canEditInvoice = (inv: Pick<Invoice, 'status' | 'fromQuickBooks' | 'invoiceDate'>, role: string | undefined): boolean =>
  canKeepBooks(role) && inv.status === 'issued' && !inv.fromQuickBooks && (role === 'owner' || inv.invoiceDate >= `${todayIso().slice(0, 7)}-01`);

export function InvoicePage() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const { id = '' } = useParams();
  const qc = useQueryClient();
  // Void, or Delete (decision 193: gone from every list, a trail kept on Activity)
  const [mode, setMode] = useState<'void' | 'delete' | null>(null);
  const voiding = mode !== null;
  const setVoiding = (on: boolean) => setMode(on ? 'void' : null);
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [keepCredits, setKeepCredits] = useState(false);
  // the invoice's credit lines (decision 188) go with the void: said, then confirmed
  const [dropCreditLines, setDropCreditLines] = useState(false);
  // what happens to the invoice's order (not delivered yet): asked, never assumed (end-user test)
  const [orderAfter, setOrderAfter] = useState<'keep' | 'cancel' | null>(null);
  // the email, written over the invoice with what the customer will see beside it (decision 171)
  const [emailing, setEmailing] = useState(false);
  const query = useQuery({ queryKey: qk.invoice(id), queryFn: ({ signal }) => api.invoice(id, signal) });
  const paca = useQuery({ queryKey: qk.pacaSettings, queryFn: ({ signal }) => api.pacaSettings(signal), staleTime: 60 * 60_000, retry: false });
  const company = useQuery({ queryKey: qk.companySettings, queryFn: ({ signal }) => api.companySettings(signal), staleTime: 60 * 60_000, retry: false });
  const doDelete = useMutation({
    mutationFn: (why: string) => api.deleteInvoice(id, why, orderAfter ?? undefined, dropCreditLines),
    onSuccess: () => {
      const inv = query.data;
      qc.removeQueries({ queryKey: qk.invoice(id) });
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['orders'] });
      void qc.invalidateQueries({ queryKey: ['customers'] });
      navigate(inv ? `/customers/${inv.customerId}` : '/invoices', { replace: true });
    },
  });
  const doVoid = useMutation({
    mutationFn: (why: string) => api.voidInvoice(id, why, keepCredits, orderAfter ?? undefined, dropCreditLines),
    onSuccess: (inv) => {
      qc.setQueryData(qk.invoice(id), inv);
      setVoiding(false);
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['orders'] });
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
  const books = canKeepBooks(me?.role);
  const switched = useBooksSwitched();
  // an invoice whose order has not been delivered: the void asks whether the order still goes out
  const asksOrder = !!query.data?.orderId && !!query.data.orderToGo;
  // the API refuses a void of an invoice with credits until the office confirms they stay on account
  const act = mode === 'delete' ? doDelete : doVoid;
  const creditsAsked = mode === 'void' && doVoid.error instanceof ApiError && !!doVoid.error.problem.errors?.some((e) => e.path === 'keepCredits');
  const creditLinesAsked = act.error instanceof ApiError && !!act.error.problem.errors?.some((e) => e.path === 'dropCreditLines');
  // "Save and print" on the invoice screen lands here with ?print=1: print once the invoice is on screen
  const [params, setParams] = useSearchParams();
  const printed = useRef(false);
  // the letterhead and the PACA terms are on the paper before it prints
  const printNow = params.get('print') === '1' && !!query.data && !company.isPending && !paca.isPending;
  useEffect(() => {
    if (!printNow || printed.current) return;
    printed.current = true;
    // the timer is never cancelled by the re-render that clearing the address causes
    setTimeout(() => {
      window.print();
      setParams({}, { replace: true });
    }, 300);
  }, [printNow, setParams]);

  return (
    <QueryView query={query}>
      {(inv: Invoice) => (
        <div className="stack invoice-view">
          <PageHead title={`${t('invoice')} ${inv.invoiceNo}${inv.test ? ` · ${t('testTag')}` : ''}`} back={`/customers/${inv.customerId}`} />
          {inv.test ? <PracticeBanner /> : null}
          <QboWriteChip invoice={inv} />
          {/* what is left on it, on the screen only (the printed invoice ends with the total, JL) */}
          {inv.status === 'issued' && inv.balance !== null ? (
            <p className="inv-balance no-print" data-testid="inv-balance">
              {inv.total - inv.balance > 0.004 ? <>{t('paidAndCredited')} {formatMoney(inv.total - inv.balance, intlLocale)} · </> : null}
              {/* lowered after it was paid: the difference is the customer's money on account, never a balance below zero */}
              {inv.balance < -0.004 ? <strong>{t('overpaidOnAccount').replace('{amount}', formatMoney(-inv.balance, intlLocale))}</strong> : <strong>{t('balanceDue')} {formatMoney(inv.balance, intlLocale)}</strong>}
            </p>
          ) : null}
          <div className="inv-actions no-print">
            {inv.status === 'issued' ? (
              <button type="button" className="btn btn-primary" onClick={() => window.print()}>
                {t('print')}
              </button>
            ) : null}
            {/* a QuickBooks invoice is paid and voided in QuickBooks (decision 115); so is one the app copies there, until the switch (decision 185) */}
            {books && inv.status === 'issued' && (inv.balance === null || inv.balance > 0.004) && (switched || (!inv.fromQuickBooks && !inv.quickbooks)) ? (
              <Link to={`/customers/${inv.customerId}/statement?invoice=${inv.id}${inv.test ? '&practice=1' : ''}`} className="btn btn-secondary">
                {t('recordPayment')}
              </Link>
            ) : null}
            {books && inv.status === 'issued' ? (
              <button type="button" className="btn btn-secondary" onClick={() => setEmailing(true)}>
                {t('emailButton')}
              </button>
            ) : null}
            {/* change prices, quantities and lines of a saved invoice (decision 170); one from QuickBooks changes there */}
            {canEditInvoice(inv, me?.role) ? (
              <Link to={`/invoices/${inv.id}/edit`} className="btn btn-secondary">
                {t('editInvoice')}
              </Link>
            ) : null}
            {inv.orderId ? (
              <Link to={`/orders/${inv.orderId}`} className="btn btn-ghost">
                {t('order')} #{inv.orderNo}
              </Link>
            ) : null}
            {books && inv.status === 'issued' && !inv.fromQuickBooks && !voiding ? (
              <button type="button" className="btn btn-ghost inv-void-btn" onClick={() => setVoiding(true)}>
                {t('voidInvoice')}
              </button>
            ) : null}
            {books && (inv.status === 'issued' || inv.status === 'void') && !inv.fromQuickBooks && !voiding ? (
              <button type="button" className="btn btn-ghost inv-void-btn" onClick={() => setMode('delete')}>
                {t('deleteInvoice')}
              </button>
            ) : null}
          </div>
          {voiding ? (
            <form
              className="card stack-sm no-print"
              onSubmit={(e) => {
                e.preventDefault();
                if (reason.trim() && (!asksOrder || orderAfter || inv.status !== 'issued')) act.mutate(reason.trim());
              }}
            >
              {mode === 'delete' ? <p className="note" data-testid="delete-hint">{t(inv.status === 'void' ? 'deleteVoidHint' : 'deleteInvoiceHint')}</p> : null}
              <label className="field">
                <span>{mode === 'delete' ? t('deleteReason') : t('voidReason')}</span>
                <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
              </label>
              {asksOrder && inv.status === 'issued' ? (
                <fieldset className="stack-sm" data-testid="void-order">
                  <legend>{t(mode === 'delete' ? 'deleteOrderAsk' : 'voidOrderAsk').replace('{no}', String(inv.orderNo))}</legend>
                  <label className="check">
                    <input type="radio" name="void-order" checked={orderAfter === 'cancel'} onChange={() => setOrderAfter('cancel')} />
                    <span>{t('voidOrderCancel')}</span>
                  </label>
                  <label className="check">
                    <input type="radio" name="void-order" checked={orderAfter === 'keep'} onChange={() => setOrderAfter('keep')} />
                    <span>{t('voidOrderKeep')}</span>
                  </label>
                </fieldset>
              ) : null}
              <div className="picker-head">
                <button type="button" className="btn btn-secondary" onClick={() => setVoiding(false)}>
                  {t('cancel')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={act.isPending || !reason.trim() || (asksOrder && inv.status === 'issued' && !orderAfter)}>
                  {mode === 'delete' ? t('deleteInvoice') : t('voidInvoice')}
                </button>
              </div>
              {creditLinesAsked ? (
                <>
                  <p className="flash" role="alert">{t('voidCreditLinesAsked')}</p>
                  <label className="check">
                    <input type="checkbox" checked={dropCreditLines} onChange={(e) => setDropCreditLines(e.target.checked)} />
                    <span>{t('voidDropCreditLines')}</span>
                  </label>
                </>
              ) : creditsAsked ? (
                <>
                  <p className="flash" role="alert">{t('voidCreditsAsked')}</p>
                  <label className="check">
                    <input type="checkbox" checked={keepCredits} onChange={(e) => setKeepCredits(e.target.checked)} />
                    <span>{t('voidKeepCredits')}</span>
                  </label>
                </>
              ) : act.error ? (
                <ErrorBox error={act.error} />
              ) : null}
            </form>
          ) : null}
          {inv.status === 'void' ? (
            <p className="muted no-print">
              {t('voided')}: {inv.voidReason}
            </p>
          ) : null}
          {me?.role === 'owner' && company.data && !company.data.addressLine1 ? (
            <p className="note no-print">
              <Link to="/settings/paca">{t('letterheadMissing')}</Link>
            </p>
          ) : null}

          <InvoiceDocument inv={inv} paca={paca.data} company={company.data} />
          {emailing ? (
            <Dialog title={`${t('emailInvoice')} ${inv.invoiceNo} · ${inv.customerName}`} onClose={() => setEmailing(false)} wide>
              <EmailPanel target={{ kind: 'invoice', id: inv.id }} customerId={inv.customerId} preview />
            </Dialog>
          ) : null}

          {books && inv.status === 'issued' ? (
            <div className="two-up no-print">
              {!inv.fromQuickBooks ? <ShortsPanel invoice={inv} /> : null}
              <EmailPanel target={{ kind: 'invoice', id: inv.id }} customerId={inv.customerId} historyOnly onCompose={() => setEmailing(true)} />
            </div>
          ) : null}
        </div>
      )}
    </QueryView>
  );
}

/** The invoice as it prints (also one per page on the route's print): the shared invoice sheet with the proof of delivery. */
export function InvoiceDocument({ inv, paca, company }: { inv: Invoice; paca: PacaSettings | undefined; company: CompanySettings | undefined }) {
  return <InvoicePaper inv={inv} paca={paca} company={company} headingLevel={2} delivery={inv.delivery} {...(inv.delivery ? { signatureUrl: api.stopSignatureUrl(inv.delivery.stopId) } : {})} />;
}
